import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, interval, Subscription } from 'rxjs';
import { Auth } from '@angular/fire/auth';
import { Router } from '@angular/router';
import { AuthenticationService } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class TokenManagerService {
  private tokenExpirySubject = new BehaviorSubject<boolean>(false);
  private refreshSubscription?: Subscription;
  private checkInterval = 5 * 60 * 1000; // Check every 5 minutes
  private auth = inject(Auth);
  private router = inject(Router);
  private authenticationService = inject(AuthenticationService);

  public tokenExpiry$ = this.tokenExpirySubject.asObservable();

  constructor() {
    this.startTokenExpiryCheck();
  }

  /**
   * Start checking token expiry at regular intervals
   */
  private startTokenExpiryCheck(): void {
    this.refreshSubscription = interval(this.checkInterval).subscribe(() => {
      this.checkAndRefreshToken();
    });
  }

  /**
   * Check if token needs refresh and refresh it automatically
   */
  private async checkAndRefreshToken(): Promise<void> {
    const user = this.auth.currentUser;
    if (!user) return;

    try {
      // Get ID token result to check expiry
      const tokenResult = await user.getIdTokenResult();
      const expirationTime = new Date(tokenResult.expirationTime).getTime();
      const now = Date.now();
      const fiveMinutes = 5 * 60 * 1000;

      // Refresh if token expires within 5 minutes
      if (expirationTime - now < fiveMinutes) {
        console.log('Token expiring soon, refreshing...');
        await user.getIdToken(true); // Force refresh
        console.log('Token refreshed successfully');
        this.tokenExpirySubject.next(false);
      }
    } catch (error) {
      console.error('Automatic token refresh failed:', error);
      this.tokenExpirySubject.next(true);

      // If refresh fails, logout the user
      await this.auth.signOut();
      this.router.navigate(['/auth/login']);
    }
  }

  /**
   * Manually refresh the token
   */
  public async refreshToken(): Promise<string> {
    const user = this.auth.currentUser;
    if (!user) {
      throw new Error('No authenticated user');
    }

    try {
      const token = await user.getIdToken(true);
      this.tokenExpirySubject.next(false);
      return token;
    } catch (error) {
      this.tokenExpirySubject.next(true);
      throw error;
    }
  }

  /**
   * Check if the current token is expired
   */
  public async isTokenExpired(): Promise<boolean> {
    const user = this.auth.currentUser;
    if (!user) return true;

    try {
      const tokenResult = await user.getIdTokenResult();
      const expirationTime = new Date(tokenResult.expirationTime).getTime();
      return Date.now() >= expirationTime;
    } catch {
      return true;
    }
  }

  /**
   * Stop the automatic token refresh check
   */
  public stopTokenExpiryCheck(): void {
    if (this.refreshSubscription) {
      this.refreshSubscription.unsubscribe();
    }
  }

  ngOnDestroy() {
    this.stopTokenExpiryCheck();
  }
}